import React from 'react'

import { lazily } from 'react-lazily'
import { Navigate, Route, Routes } from 'react-router-dom'

import { Loader } from '../components/common/Loader'

import { PrivateRoute } from './PrivateRoutes'
import { ROUTE_NAMES } from './routeNames'
import { CalendarPage } from '../pages/calendar'
import { PortfolioContainer } from '../pages/portfolio'
import { ReportsContainer } from '../pages/reports'

const { ExchangeOrderContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/exchange/containers/ExchangeOrderContainer'),
)
const { AccountView } = lazily(() => import('@UI/crypto_exchange/desktop/pages/account/AccountView'))

const { SpotOrderContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/spot/containers/SpotOrderContainer'),
)
const { MarketsContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/markets/containers/MarketsContainer'),
)
const { SimpleEarnContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/simple_earn/containers/SimpleEarnContainer'),
)
const { HistoryContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/history/containers/HistoryContainer'),
)

const { LoginContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/authorization_pages/containers/LoginContainer'),
)
const { VerificationContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/authorization_pages/containers/VerificationContainer'),
)
const { RegistrationContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/authorization_pages/containers/RegistrationContainer'),
)

const { GoogleAuthenticator } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/google_authenticator/containers/GoogleAuthenticator'),
)

const { DepositContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/deposit/containers/DepositContainer'),
)

// const { DepositItemContainer } = lazily(
//   () => import('@UI/crypto_exchange/desktop/pages/deposit_item/AccountOrdersContainer/DepositItemContainer'),
// )
// const { WithdrawalItemContainer } = lazily(
//   () => import('@UI/crypto_exchange/desktop/pages/withdrawal_item/AccountOrdersContainer/WithdrawalItemContainer'),
// )

const { ResetPasswordLinkContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/authorization_pages/containers/ResetPasswordLinkContainer'),
)
const { ResetPasswordContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/authorization_pages/containers/ResetPasswordContainer'),
)

const { FiatDeposit } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/fiat_deposit/containers/FiatDepositContainer'),
)

const { DepositResult } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/deposit_result/containers/DepositResultContainer'),
)

const { ChangePassword } = lazily(() => import('@UI/crypto_exchange/desktop/pages/change_password/containers'))

const { DeviceManagement } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/device_management/containers/DeviceManagement'),
)

const { ChangeEmailAuthenticatorContainer } = lazily(
  () =>
    import('@UI/crypto_exchange/desktop/pages/change_email_authenticator/containers/ChangeEmailAuthenticatorContainer'),
)

const { NotFoundPageContainer } = lazily(() => import('@UI/crypto_exchange/desktop/pages/not_found_page'))

const { P2PContainer } = lazily(() => import('@UI/crypto_exchange/desktop/pages/P2P/containers/P2PContainer'))

const { P2POfferCreateContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/P2P/offer/create/containers/P2POfferCreateContainer'),
)

const { P2POfferUpdateContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/P2P/offer/update/containers/P2POfferUpdateContainer'),
)

const { P2POffersContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/P2P/offer/list/public/containers/P2POffersContainer'),
)

const { AccountP2pOffersContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/P2P/offer/list/private/containers/AccountP2pOffersContainer'),
)

const { P2POrdersContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/P2P/order/list/private/containers/P2POrdersContainer'),
)

const { P2PAdvertiserContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/P2P/user/public/containers/P2PAdvertiserContainer'),
)

const { P2PUserCenterContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/P2P/user/private/containers/P2PUserCenterContainer'),
)

const { OrderContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/P2P/order/create/containers/OrderContainer'),
)

const { LoanContainer } = lazily(() => import('@UI/crypto_exchange/desktop/pages/loan/containers/LoanContainer'))

const { ListContainer } = lazily(() => import('@UI/crypto_exchange/desktop/pages/loan/active/containers/ListContainer'))

const { LoanListContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/loan/list/containers/LoanListContainer'),
)

const { LoanDataContainer } = lazily(
  () => import('@UI/crypto_exchange/desktop/pages/loan/data/containers/LoanDataContainer'),
)

const { DiscoverPage } = lazily(() => import('@UI/crypto_exchange/desktop/pages/discover'))
const { ChartsContainer } = lazily(() => import('@UI/crypto_exchange/desktop/pages/charts'))

export const RouterContainer = () => {
  return (
    <React.Suspense fallback={<Loader />}>
      <Routes>
        <Route path={ROUTE_NAMES.HOME} element={<Navigate to={ROUTE_NAMES.TRADING} replace />} />
        <Route path={ROUTE_NAMES.LOGIN} element={<LoginContainer />} />
        <Route path={ROUTE_NAMES.TWO_FA} element={<VerificationContainer />} />
        <Route path={ROUTE_NAMES.REGISTRATION} element={<RegistrationContainer />} />
        <Route path={ROUTE_NAMES.CALENDAR} element={<CalendarPage />} />
        <Route path={ROUTE_NAMES.RESET_PASSWORD_LINK} element={<ResetPasswordLinkContainer />} />
        <Route path={ROUTE_NAMES.RESET_PASSWORD} element={<ResetPasswordContainer />} />
        <Route path={ROUTE_NAMES.TRADING_WITH_PAIR} element={<SpotOrderContainer />} />
        <Route path={ROUTE_NAMES.TRADING} element={<SpotOrderContainer />} />
        <Route path={ROUTE_NAMES.CHARTS} element={<ChartsContainer />} />
        <Route path={ROUTE_NAMES.PORTFOLIO} element={<PortfolioContainer />} />

        <Route path={ROUTE_NAMES.MARKETS_LIST} element={<MarketsContainer />} />

        <Route path={ROUTE_NAMES.DISCOVER} element={<DiscoverPage />} />

        <Route path={ROUTE_NAMES.SIMPLE_EARN} element={<SimpleEarnContainer />} />

        <Route path={ROUTE_NAMES.P2P} element={<P2PContainer />}>
          <Route path={ROUTE_NAMES.P2P_WITH_PAIR} element={<P2POffersContainer />} />
          <Route path={ROUTE_NAMES.P2P_ADVERTISER_DETAIL} element={<P2PAdvertiserContainer />} />
        </Route>

        <Route path={ROUTE_NAMES.LOANS} element={<LoanContainer />}>
          <Route path="" element={<LoanListContainer />} />
          <Route path={ROUTE_NAMES.LOANS_DATA} element={<LoanDataContainer />} />
        </Route>

        <Route element={<PrivateRoute />}>
          {/*<Route path={ROUTE_NAMES.ACCOUNT} element={<AccountView />}>*/}
          {/*</Route>*/}

          <Route path={ROUTE_NAMES.HISTORY} element={<HistoryContainer />} />
          <Route path={ROUTE_NAMES.REPORTS} element={<ReportsContainer />} />

          <Route path={ROUTE_NAMES.ENABLE_GOOGLE_AUTHENTICATOR} element={<GoogleAuthenticator type={'enable'} />} />
          <Route path={ROUTE_NAMES.DISABLE_GOOGLE_AUTHENTICATOR} element={<GoogleAuthenticator type={'disable'} />} />
          <Route path={ROUTE_NAMES.CHANGE_EMAIL_AUTHENTICATOR} element={<ChangeEmailAuthenticatorContainer />} />
          <Route path={ROUTE_NAMES.CHANGE_PASSWORD} element={<ChangePassword />} />
          <Route path={ROUTE_NAMES.DEVICE_MANAGEMENT} element={<DeviceManagement />} />
          <Route path={ROUTE_NAMES.EXCHANGE_WITH_PAIR} element={<ExchangeOrderContainer />} />
          <Route path={ROUTE_NAMES.EXCHANGE} element={<ExchangeOrderContainer />} />
          <Route path={ROUTE_NAMES.DEPOSIT} element={<DepositContainer />} />
          <Route path={ROUTE_NAMES.DEPOSIT_ITEM} element={<DepositContainer />} />
          <Route path={ROUTE_NAMES.FIAT_DEPOSIT} element={<FiatDeposit />} />
          <Route path={ROUTE_NAMES.DEPOSIT_RESULT} element={<DepositResult />} />
          <Route path={ROUTE_NAMES.P2P} element={<P2PContainer />}>
            <Route path={ROUTE_NAMES.P2P_OFFERS_OPEN} element={<P2POfferCreateContainer />} />
            <Route path={ROUTE_NAMES.P2P_OFFERS_UPDATE} element={<P2POfferUpdateContainer />} />
            <Route path={ROUTE_NAMES.P2P_USER_CENTER} element={<P2PUserCenterContainer />} />
            <Route element={<PrivateRoute />}>
              <Route path={ROUTE_NAMES.P2P_MY_OFFERS} element={<AccountP2pOffersContainer />} />
              <Route path={ROUTE_NAMES.P2P_ORDERS} element={<P2POrdersContainer />} />
              <Route path={ROUTE_NAMES.P2P_ORDER} element={<OrderContainer />} />
            </Route>
          </Route>
          <Route path={ROUTE_NAMES.LOANS} element={<LoanContainer />}>
            <Route path={ROUTE_NAMES.ACTIVE_LOANS} element={<ListContainer />} />
          </Route>
        </Route>
        <Route path="*" element={<NotFoundPageContainer />} />
      </Routes>
    </React.Suspense>
  )
}
